import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useProject } from '../context/ProjectContext';
import ComponentEditor from './ComponentEditor';
import { Button } from './ui/button';
import { 
  Move, 
  X, 
  Maximize2, 
  Minimize2,
  PanelRight,
  PanelBottom,
  GripVertical
} from 'lucide-react';

const DockableEditor = ({ 
  defaultDockPosition = 'right', // 'right', 'bottom', 'floating'
  onDockChange,
  treeRef // Ref zum Projektbaum für Positionierung
}) => {
  const { selectedNode } = useProject();
  const [dockPosition, setDockPosition] = useState(defaultDockPosition);
  const [floatingPosition, setFloatingPosition] = useState({ x: 400, y: 100 });
  const [floatingSize, setFloatingSize] = useState({ width: 400, height: 500 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const editorRef = useRef(null);

  // Drag-Start Handler
  const handleDragStart = useCallback((e) => {
    if (dockPosition !== 'floating') return;
    e.preventDefault();
    const rect = editorRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    setIsDragging(true);
  }, [dockPosition]);

  // Drag-Move Handler
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setFloatingPosition({
        x: Math.max(0, e.clientX - dragOffset.x),
        y: Math.max(0, e.clientY - dragOffset.y)
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      
      // Snap-to-dock Logik
      const x = floatingPosition.x;
      const y = floatingPosition.y;
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      
      // Snap rechts wenn am rechten Rand (innerhalb 50px)
      if (x + floatingSize.width > windowWidth - 50) {
        setDockPosition('right');
        onDockChange?.('right');
      }
      // Snap unten wenn am unteren Rand
      else if (y + floatingSize.height > windowHeight - 100) {
        setDockPosition('bottom');
        onDockChange?.('bottom');
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, floatingPosition, floatingSize, onDockChange]);

  // Resize Handler
  const handleResizeStart = useCallback((e) => {
    if (dockPosition !== 'floating') return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
  }, [dockPosition]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(300, e.clientX - floatingPosition.x);
      const newHeight = Math.max(300, e.clientY - floatingPosition.y);
      setFloatingSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, floatingPosition]);

  // Dock Position ändern
  const handleDock = (position) => {
    setDockPosition(position);
    onDockChange?.(position);
    if (position === 'floating') {
      // Setze initiale floating Position basierend auf Fenster
      setFloatingPosition({ 
        x: window.innerWidth - floatingSize.width - 50,
        y: 100 
      });
    }
  };

  // Undock (zu floating wechseln)
  const handleUndock = () => {
    setDockPosition('floating');
    onDockChange?.('floating');
    setFloatingPosition({ 
      x: window.innerWidth / 2 - floatingSize.width / 2,
      y: window.innerHeight / 4 
    });
  };

  // Wenn nichts ausgewählt ist, zeige Platzhalter
  if (!selectedNode) {
    if (dockPosition === 'floating') return null;
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 text-gray-400 text-sm p-4 text-center">
        <div>
          <p className="font-medium">Komponenten-Editor</p>
          <p className="text-xs mt-1">Wählen Sie eine Komponente aus</p>
        </div>
      </div>
    );
  }

  // Render für angedockten Zustand (rechts oder unten)
  if (dockPosition !== 'floating') {
    return (
      <div className="flex flex-col h-full bg-white border-t border-gray-200">
        {/* Header mit Dock-Controls */}
        <div className="flex items-center justify-between px-2 py-1 bg-gray-100 border-b border-gray-200">
          <span className="text-xs font-medium text-gray-600">Eigenschaften</span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={handleUndock}
              title="Frei bewegen"
            >
              <Move className="w-3 h-3" />
            </Button>
            {dockPosition === 'right' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => handleDock('bottom')}
                title="Unten andocken"
              >
                <PanelBottom className="w-3 h-3" />
              </Button>
            )}
            {dockPosition === 'bottom' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => handleDock('right')}
                title="Rechts andocken"
              >
                <PanelRight className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        {/* Editor Content */}
        <div className="flex-1 overflow-auto">
          <ComponentEditor />
        </div>
      </div>
    );
  }

  // Render für floating Zustand
  return (
    <div
      ref={editorRef}
      className={`fixed bg-white rounded-lg shadow-2xl border border-gray-300 z-50 flex flex-col ${
        isDragging ? 'cursor-grabbing' : ''
      }`}
      style={{
        left: floatingPosition.x,
        top: floatingPosition.y,
        width: floatingSize.width,
        height: floatingSize.height,
        minWidth: 300,
        minHeight: 300
      }}
    >
      {/* Floating Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 bg-emerald-600 text-white rounded-t-lg cursor-grab"
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 opacity-70" />
          <span className="text-sm font-medium">Eigenschaften</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-white hover:bg-emerald-500"
            onClick={() => handleDock('right')}
            title="Rechts andocken"
          >
            <PanelRight className="w-3 h-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-white hover:bg-emerald-500"
            onClick={() => handleDock('bottom')}
            title="Unten andocken"
          >
            <PanelBottom className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Floating Content */}
      <div className="flex-1 overflow-auto">
        <ComponentEditor />
      </div>

      {/* Resize Handle */}
      <div
        className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
        onMouseDown={handleResizeStart}
      >
        <svg viewBox="0 0 16 16" className="w-full h-full text-gray-400">
          <path d="M14 14L2 14L14 2L14 14Z" fill="currentColor" opacity="0.3" />
          <path d="M14 10L10 14" stroke="currentColor" strokeWidth="1.5" />
          <path d="M14 6L6 14" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
    </div>
  );
};

export default DockableEditor;
